import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as d3 from 'd3'

import moment from 'moment'
import { Overlays } from 'utils'
import { mapDockWindowsToShoalWindowsMinMax } from 'scenarios/calculations'
import { isDualRangePilotScenario } from 'scenarios'

function Timeline(props) {
    const { t } = useTranslation()
    const { data: raw, loading, error, thresholdMode = 'dock' } = props

    const [data, setData] = useState([])
    const [data_shoal, setData_shoal] = useState([])

    const threshold = () => -props.dockDepth + +props.tolerance
    const threshold_shoal = () => {
        let value = -props.dockDepth + +props.tolerance
        if (props.dockShoal !== undefined && props.dockShoal !== 0) {
            value = -props.dockShoal + +props.tolerance
        }
        return value
    }

    // Check if we have shoal data (pilot scenarios)
    const hasShoal = props.dockShoal !== undefined && props.dockShoal !== 0

    // Adjust top margin: more space needed when showing both shoal and dock labels
    const margin = { top: hasShoal ? 40 : 20, right: 20, bottom: 50, left: 50 }
    const width = props.width - margin.left - margin.right
    const height = props.height - margin.top - margin.bottom

    const xScale = d3.scaleTime().range([0, width])
    const select = selector =>
        selector ? d3.select(`#timeline ${selector}`) : d3.select('#timeline')
    const selectAll = selector =>
        selector ? d3.selectAll(`#timeline ${selector}`) : d3.selectAll('#timeline')

    const format = raw => {
        if (!raw || raw.length === 0) return { grouped: [], domain: [] }
        raw = raw.sort((a, b) => a.date - b.date)
        const domain = [raw[0].date, raw[raw.length - 1].date]

        const grouped = []
        let navigable,
            currentIndex = -1
        raw.forEach(d => {
            let tide = d.wlo !== 0 ? d.wlo : d.wlp > d['wlf-spine'] ? d['wlf-spine'] : d.wlp
            if (!tide) return
            const boatDraft = tide - props.boatDraft
            if (
                (boatDraft >= threshold() && navigable !== true) ||
                (boatDraft < threshold() && navigable !== false)
            ) {
                navigable = boatDraft >= threshold()
                currentIndex++
                grouped[currentIndex] = {
                    navigable,
                    boatDraft_min: boatDraft,
                    min: d.date,
                    max: null,
                }
            }
            grouped[currentIndex].max = d.date
            grouped[currentIndex].boatDraft_max = boatDraft
        })

        return { grouped, domain }
    }

    const format_shoal = raw => {
        if (!raw || raw.length === 0) return { grouped_shoal: [] }
        if (!props.dockShoal && props.dockShoal === 0) return { grouped_shoal: [] }
        raw = raw.sort((a, b) => a.date - b.date)

        const grouped_shoal = []
        let navigable,
            currentIndex = -1
        raw.forEach(d => {
            let tide = d.wlo !== 0 ? d.wlo : d.wlp > d['wlf-spine'] ? d['wlf-spine'] : d.wlp
            if (!tide) return
            const boatDraft = tide - props.boatDraft
            if (
                (boatDraft >= threshold_shoal() && navigable !== true) ||
                (boatDraft < threshold_shoal() && navigable !== false)
            ) {
                navigable = boatDraft >= threshold_shoal()
                currentIndex++
                grouped_shoal[currentIndex] = {
                    navigable,
                    min: d.date,
                    max: null,
                }
            }
            if (currentIndex >= 0) grouped_shoal[currentIndex].max = d.date
        })

        return { grouped_shoal }
    }

    const init = () => {
        // Add pattern definition for striped bars
        const defs = select().append('defs')
        defs.append('pattern')
            .attr('id', 'timeline-pilot-stripes')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 5)
            .attr('height', 5)
            .attr('patternTransform', 'rotate(45)')
            .append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', 0)
            .attr('y2', 5)
            .attr('stroke', '#6b4c8c')
            .attr('stroke-width', 3)

        const svg = select()
            .append('g')
            .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')

        svg.append('g')
            .attr('id', 'xAxis')
            .attr('transform', 'translate(0,' + height + ')')

        svg.append('g')
            .attr('id', 'xAxis-date')
            .attr('transform', 'translate(0,' + (height + 20) + ')')

        svg.append('text')
            .attr('class', 'axis-legend')
            .attr('transform', `translate(${width / 2}, ${height + margin.bottom})`)
            .style('text-anchor', 'middle')
            .text(t('general.date_legend'))

        svg.append('g').attr('id', 'bars')
        svg.append('g').attr('id', 'pilot-edges')
        svg.append('rect').attr('id', 'past-overlay')
        svg.append('g').attr('id', 'infos')
        svg.append('g').attr('id', 'infos-shoal')
        svg.append('text')
            .attr('id', 'details')
            .html(`${t('general.min_tide')} : <tspan id="min-tide">-</tspan>`)
            .attr('transform', `translate(${width / 2}, -20)`)
    }

    const update = () => {
        const { grouped, domain } = data
        const { grouped_shoal } = data_shoal

        // Get the data range end time to detect incomplete windows
        const dataEndTimeMs = domain && domain[1] ? new Date(domain[1]).getTime() : 0

        // Check if this window is incomplete (ends at data boundary)
        const isIncompleteEnd = d => {
            if (!d.max) return false
            const endTimeMs = new Date(d.max).getTime()
            // If end is within 5 minutes of data end, it's incomplete
            return dataEndTimeMs && Math.abs(endTimeMs - dataEndTimeMs) < 5 * 60 * 1000
        }

        // Update main group transform in case margin changed (hasShoal can change after init)
        select('g').attr('transform', `translate(${margin.left}, ${margin.top})`)

        xScale.domain(domain)

        // AXIS - update positions in case height changed
        select('#xAxis')
            .attr('transform', `translate(0, ${height})`)
            .call(d3.axisBottom(xScale).ticks(d3.timeHour.every(3), '%Hh').tickSize(0))
        select('#xAxis-date').attr('transform', `translate(0, ${height + 20})`)
        select('.axis-legend').attr('transform', `translate(${width / 2}, ${height + 45})`)

        select('#xAxis-date').call(
            d3.axisBottom(xScale).ticks(d3.timeDay.every(1), '%d-%m').tickSize(0)
        )

        // BARS
        // barOffset: distance from bottom of content area to bar center
        // When hasShoal, move bars up to make room for shoal labels below
        const barOffset = hasShoal ? 38 : 21
        const attr = {
            w: 1,
            h: 30,
            class: d => {
                let base = 'bar'
                if (d.navigable) base += ' ok'
                if (!props.startHour) return base

                const date = moment(props.startHour)
                const range = [moment(d.min), moment(d.max)]
                if (date.isBetween(range[0], range[1], undefined, '[]')) base += ' active'
                return base
            },
        }

        const bars = select('#bars').selectAll('.bar').data(grouped)

        // Exit
        bars.exit().remove()

        // Enter
        bars.enter()
            .append('line')
            .attr('class', attr.class)
            .attr('data-area', d => new Date(d.max).getTime())
            .attr('x1', d => xScale(d.min))
            .attr('x2', d => xScale(d.max))
            .attr('y1', height - barOffset)
            .attr('y2', height - barOffset)
            .attr('stroke-width', attr.h)

        // Update
        bars.transition()
            .attr('class', attr.class)
            .attr('data-area', d => new Date(d.max).getTime())
            .attr('x1', d => xScale(d.min))
            .attr('x2', d => xScale(d.max))

        // PILOT EDGES - render striped edges for pilot-only portions in dock or both mode
        select('#pilot-edges').selectAll('.pilot-edge').remove()

        if (thresholdMode !== 'shoal' && props.startHour && grouped_shoal.length > 0) {
            const navigableWindows = grouped.filter(g => g.navigable === true)
            const navigableShoalWindows = grouped_shoal.filter(g => g.navigable === true)

            // Find selected window
            const selectedIndex = navigableWindows.findIndex(w => {
                const date = moment(props.startHour)
                return date.isBetween(moment(w.min), moment(w.max), undefined, '[]')
            })

            if (selectedIndex >= 0) {
                const maxNumberedAreas = 4
                const relevantWindows = navigableWindows.slice(
                    selectedIndex,
                    selectedIndex + maxNumberedAreas
                )

                relevantWindows.forEach(dockWindow => {
                    const dockMin = new Date(dockWindow.min).getTime()
                    const dockMax = new Date(dockWindow.max).getTime()

                    // Find corresponding shoal window
                    const shoalWindow = navigableShoalWindows.find(sw => {
                        const swMin = new Date(sw.min).getTime()
                        const swMax = new Date(sw.max).getTime()
                        return swMin < dockMax && swMax > dockMin
                    })

                    if (shoalWindow) {
                        const shoalMin = new Date(shoalWindow.min).getTime()
                        const shoalMax = new Date(shoalWindow.max).getTime()

                        // Left edge: dock start to shoal start (rising tide)
                        // Only render for Scenario 4 (pilot up/down), not Scenario 3 (pilot desc only)
                        if (
                            isDualRangePilotScenario(props.calculationMethod) &&
                            dockMin < shoalMin
                        ) {
                            select('#pilot-edges')
                                .append('line')
                                .attr('class', 'pilot-edge')
                                .attr('x1', xScale(new Date(dockMin)))
                                .attr('x2', xScale(new Date(shoalMin)))
                                .attr('y1', height - barOffset)
                                .attr('y2', height - barOffset)
                                .attr('stroke-width', attr.h)
                                .attr('stroke', 'url(#timeline-pilot-stripes)')
                                .attr('opacity', 0.6)
                                .style('cursor', 'pointer')
                                .on('click', async () => {
                                    if (props.handleAreaClick) {
                                        // Trigger click on the parent dock window
                                        const bar = select(`#bars .bar[data-area="${dockMax}"]`)
                                        if (bar.node()) {
                                            bar.node().dispatchEvent(
                                                new Event('click', { bubbles: true })
                                            )
                                        }
                                    }
                                })
                        }

                        // Right edge: shoal end to dock end
                        if (shoalMax < dockMax) {
                            select('#pilot-edges')
                                .append('line')
                                .attr('class', 'pilot-edge')
                                .attr('x1', xScale(new Date(shoalMax)))
                                .attr('x2', xScale(new Date(dockMax)))
                                .attr('y1', height - barOffset)
                                .attr('y2', height - barOffset)
                                .attr('stroke-width', attr.h)
                                .attr('stroke', 'url(#timeline-pilot-stripes)')
                                .attr('opacity', 0.6)
                                .style('cursor', 'pointer')
                                .on('click', async () => {
                                    if (props.handleAreaClick) {
                                        // Trigger click on the parent dock window
                                        const bar = select(`#bars .bar[data-area="${dockMax}"]`)
                                        if (bar.node()) {
                                            bar.node().dispatchEvent(
                                                new Event('click', { bubbles: true })
                                            )
                                        }
                                    }
                                })
                        }
                    }
                })
            }
        }

        // INFORMATIONS
        const infos = select('#infos')
            .selectAll('.navigable')
            .data(grouped.filter(g => g.navigable === true))

        // Exit
        infos.exit().remove()

        // Enter
        // Position: with pilot = bottom of bar, without pilot = top of bar
        const dockInfoY = hasShoal
            ? height - barOffset + attr.h / 2 // bottom of bar
            : height - barOffset - attr.h / 2 // top of bar
        const dockTimeY = hasShoal ? 16 : -10 // below or above squares
        const dockDescrY = hasShoal ? -(attr.h + 25) : -25 // above everything

        const infosEnter = infos
            .enter()
            .append('g')
            .attr('class', d => {
                const diff = moment(d.max).diff(moment(props.startHour), 'minutes')
                if (diff <= 2 && diff >= -2) return 'navigable active'
                return 'navigable'
            })
            .attr('data-area', d => new Date(d.max).getTime())
            .attr('transform', d => `translate(${xScale(d.min)}, ${dockInfoY})`)

        const TIME_TICKER_WIDTH = 28
        // Helper to get class for dock info elements based on width and completeness
        // In pilot scenarios (hasShoal), always show dock times since they're valid pilot departure windows
        const getDockStartClass = (d, baseClass) => {
            if (hasShoal) return baseClass // Always show in pilot scenarios
            if (xScale(d.max) - xScale(d.min) <= TIME_TICKER_WIDTH * 2) return `${baseClass} hidden`
            return baseClass
        }
        const getDockEndClass = (d, baseClass) => {
            if (isIncompleteEnd(d)) return `${baseClass} hidden`
            if (hasShoal) return baseClass // Always show in pilot scenarios
            if (xScale(d.max) - xScale(d.min) <= TIME_TICKER_WIDTH * 2) return `${baseClass} hidden`
            return baseClass
        }

        infosEnter
            .append('rect')
            .attr('class', d => getDockStartClass(d, 'r-start'))
            .attr('x', 0)
            .attr('y', 0)

        infosEnter
            .append('rect')
            .attr('class', d => getDockEndClass(d, 'r-end'))
            .attr('x', d => xScale(d.max) - xScale(d.min))
            .attr('y', 0)

        infosEnter
            .append('text')
            .attr('class', 'descr')
            .text(t('general.navigable_zone'))
            .attr(
                'transform',
                d => `translate(${(xScale(d.max) - xScale(d.min)) / 2}, ${dockDescrY})`
            )

        infosEnter
            .append('text')
            .attr('class', d => getDockStartClass(d, 'd-start'))
            .text(d => moment(d.min).format('HH:mm'))
            .attr('transform', `translate(0, ${dockTimeY})`)

        infosEnter
            .append('text')
            .attr('class', d => getDockEndClass(d, 'd-end'))
            .text(d => moment(d.max).format('HH:mm'))
            .attr('transform', d => `translate(${xScale(d.max) - xScale(d.min)}, ${dockTimeY})`)

        // Update
        infos
            .transition()
            .attr('class', d => {
                const diff = moment(d.max).diff(moment(props.startHour), 'minutes')
                if (diff <= 2 && diff >= -2) return 'navigable active'
                return 'navigable'
            })
            .attr('data-area', d => new Date(d.max).getTime())
            .attr('transform', d => `translate(${xScale(d.min)}, ${dockInfoY})`)

        infos.select('.r-start').attr('class', d => getDockStartClass(d, 'r-start'))
        infos
            .select('.r-end')
            .attr('x', d => xScale(d.max) - xScale(d.min))
            .attr('class', d => getDockEndClass(d, 'r-end'))

        infos
            .select('.descr')
            .attr(
                'transform',
                d => `translate(${(xScale(d.max) - xScale(d.min)) / 2}, ${dockDescrY})`
            )

        infos
            .select('.d-start')
            .text(d => moment(d.min).format('HH:mm'))
            .attr('class', d => getDockStartClass(d, 'd-start'))

        infos
            .select('.d-end')
            .text(d => moment(d.max).format('HH:mm'))
            .attr('transform', d => `translate(${xScale(d.max) - xScale(d.min)}, ${dockTimeY})`)
            .attr('class', d => getDockEndClass(d, 'd-end'))

        // SHOAL INFORMATIONS (above bars - sans pilote / narrower window)
        if (grouped_shoal && grouped_shoal.length > 0) {
            const now = moment()
            const infosShoal = select('#infos-shoal')
                .selectAll('.navigable-shoal')
                .data(grouped_shoal.filter(g => g.navigable === true && moment(g.max).isAfter(now)))

            // Exit
            infosShoal.exit().remove()

            // For narrow windows, show single centered square and time
            // For wide windows, show start and end squares with times
            const isNarrowShoal = d => xScale(d.max) - xScale(d.min) <= TIME_TICKER_WIDTH * 2

            // Helper to get class for shoal info elements
            const getShoalEndClass = (d, baseClass) => {
                if (isIncompleteEnd(d)) return `${baseClass} hidden`
                return baseClass
            }
            const getShoalEndTextClass = (d, baseClass) => {
                if (isIncompleteEnd(d)) return `${baseClass} hidden`
                if (isNarrowShoal(d)) return `${baseClass} hidden`
                return baseClass
            }
            // For center text, hide if incomplete (can't show range without end)
            const getShoalCenterClass = d => {
                if (isIncompleteEnd(d)) return 'd-center hidden'
                return isNarrowShoal(d) ? 'd-center' : 'd-center hidden'
            }

            // Enter
            const infosShoalEnter = infosShoal
                .enter()
                .append('g')
                .attr('class', d => {
                    if (!props.startHour) return 'navigable-shoal'
                    const date = moment(props.startHour)
                    const range = [moment(d.min), moment(d.max)]
                    if (date.isBetween(range[0], range[1], undefined, '[]'))
                        return 'navigable-shoal active'
                    return 'navigable-shoal'
                })
                .attr('data-area-shoal', d => new Date(d.max).getTime())
                .attr(
                    'transform',
                    d => `translate(${xScale(d.min)}, ${height - barOffset - attr.h / 2})`
                )

            // Always show start square
            infosShoalEnter.append('rect').attr('class', 'r-start').attr('x', 0).attr('y', 0)

            // Hide end square for incomplete windows
            infosShoalEnter
                .append('rect')
                .attr('class', d => getShoalEndClass(d, 'r-end'))
                .attr('x', d => xScale(d.max) - xScale(d.min))
                .attr('y', 0)

            // For wide windows: separate start and end times
            infosShoalEnter
                .append('text')
                .attr('class', d => (isNarrowShoal(d) ? 'd-start hidden' : 'd-start'))
                .text(d => moment(d.min).format('HH:mm'))
                .attr('transform', `translate(0, -10)`)

            infosShoalEnter
                .append('text')
                .attr('class', d => getShoalEndTextClass(d, 'd-end'))
                .text(d => moment(d.max).format('HH:mm'))
                .attr('transform', d => `translate(${xScale(d.max) - xScale(d.min)}, -10)`)

            // For narrow windows: centered range text "HH:mm-HH:mm"
            infosShoalEnter
                .append('text')
                .attr('class', d => getShoalCenterClass(d))
                .text(d => `${moment(d.min).format('HH:mm')} ${moment(d.max).format('HH:mm')}`)
                .attr('transform', d => `translate(${(xScale(d.max) - xScale(d.min)) / 2}, -10)`)

            // Update
            infosShoal
                .transition()
                .attr('class', d => {
                    if (!props.startHour) return 'navigable-shoal'
                    const date = moment(props.startHour)
                    const range = [moment(d.min), moment(d.max)]
                    if (date.isBetween(range[0], range[1], undefined, '[]'))
                        return 'navigable-shoal active'
                    return 'navigable-shoal'
                })
                .attr('data-area-shoal', d => new Date(d.max).getTime())
                .attr(
                    'transform',
                    d => `translate(${xScale(d.min)}, ${height - barOffset - attr.h / 2})`
                )

            infosShoal.select('.r-start').attr('class', 'r-start')
            infosShoal
                .select('.r-end')
                .attr('x', d => xScale(d.max) - xScale(d.min))
                .attr('class', d => getShoalEndClass(d, 'r-end'))

            infosShoal
                .select('.d-start')
                .text(d => moment(d.min).format('HH:mm'))
                .attr('class', d => (isNarrowShoal(d) ? 'd-start hidden' : 'd-start'))

            infosShoal
                .select('.d-end')
                .text(d => moment(d.max).format('HH:mm'))
                .attr('transform', d => `translate(${xScale(d.max) - xScale(d.min)}, -10)`)
                .attr('class', d => getShoalEndTextClass(d, 'd-end'))

            infosShoal
                .select('.d-center')
                .text(d => `${moment(d.min).format('HH:mm')} ${moment(d.max).format('HH:mm')}`)
                .attr('transform', d => `translate(${(xScale(d.max) - xScale(d.min)) / 2}, -10)`)
                .attr('class', d => getShoalCenterClass(d))
        }

        // INTERACTIONS & OTHERS DISPLAY
        // Position #details based on margin - needs to be visible above content
        const detailsY = hasShoal ? -20 : 15
        select('#details').attr('transform', `translate(${width / 2}, ${detailsY})`)

        if (hasShoal) {
            const tideShoal = (threshold_shoal() + +props.boatDraft).toFixed(2)
            const tideDock = (threshold() + +props.boatDraft).toFixed(2)
            select('#min-tide').text(
                `${tideShoal} m (${t('general.without_pilot')}) / ${tideDock} m (${t(
                    'general.with_pilot'
                )})`
            )
        } else {
            select('#min-tide').text((threshold() + +props.boatDraft).toFixed(2) + ' m')
        }

        select('#past-overlay')
            .attr('width', xScale(moment()))
            .attr('height', attr.h)
            .attr('x', 0)
            .attr('y', height - barOffset - attr.h / 2)

        const navigableWindows = grouped.filter(g => g.navigable === true)
        const maxNumberedAreas = 4

        selectAll('.bar').on('click', async (e, d) => {
            console.log(e, d)
            if (props.handleAreaClick) {
                const self = d3.select(e.target)
                //if (self.classed('active')) return
                if (!self.classed('ok')) return

                // Find clicked window index in navigable windows
                const clickedIndex = navigableWindows.findIndex(
                    w => w.min === d.min && w.max === d.max
                )

                // Calculate departure windows starting from clicked window
                const windowsFromClick =
                    clickedIndex >= 0
                        ? navigableWindows.slice(clickedIndex, clickedIndex + maxNumberedAreas)
                        : []
                const departureWindows = windowsFromClick
                    .filter(w => {
                        // Only include windows with sufficient duration (>= 1 hour)
                        const duration = new Date(w.max) - new Date(w.min)
                        return duration >= 60 * 60 * 1000
                    })
                    .slice(0, maxNumberedAreas)
                    .map(w => ({
                        start: w.min,
                        end: w.max,
                        windowTooShort: false,
                    }))

                // Calculate shoal departure windows using extracted function
                // Maps each dock window to its corresponding shoal window (if any)
                const navigableShoalWindows = grouped_shoal.filter(g => g.navigable === true)
                const shoalDepartureWindows = mapDockWindowsToShoalWindowsMinMax(
                    departureWindows,
                    navigableShoalWindows
                )

                const status = await props.handleAreaClick(
                    [
                        { date: d.min, boatDraft: d.boatDraft_min },
                        { date: d.max, boatDraft: d.boatDraft_max },
                    ],
                    grouped_shoal.filter(g => g.navigable === true),
                    1, // Selected window is always option 1
                    departureWindows,
                    shoalDepartureWindows
                )
                if (!status) return
                d3.selectAll(`[data-area]`).classed('active', false)
                d3.selectAll(`[data-area="${self.attr('data-area')}"]`).classed('active', true)
            }
        })
    }

    useEffect(() => {
        if (d3.select('#timeline').html() === '') init()
    }, [])

    useEffect(() => {
        if (!raw || !props.dockDepth || !props.tolerance || !props.boatDraft) {
            setData_shoal([])
            return setData([])
        }
        setData(format(raw))
        setData_shoal(format_shoal(raw))
    }, [raw, props.dockDepth, props.tolerance, props.boatDraft])

    useEffect(() => {
        if (data?.grouped?.length > 0) update()
    }, [data, data_shoal, props.startHour, thresholdMode])

    return (
        <div className='timeline-wrap'>
            <svg id='timeline' viewBox={`0 0 ${props.width} ${props.height}`} />
            <Overlays loading={loading} error={error} />
        </div>
    )
}

export default Timeline
